import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useRouter } from 'next/router';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import apiFetch from ':src/utils/apiFetch';
import UserState from ':src/hooks/UserState';
import Button from './Button';
import bugsnag from ':src/lib/bugsnag';
import { Solution } from ':src/types';
import InputField from './InputField';

const CODEPEN_REGEX =
  /^(?:https?:\/\/)?codepen.io\/([a-z0-9-_]+)\/pen\/([a-zA-Z0-9]+)\/?$/;

const schema = yup.object({
  title: yup.string().required('Please title your solution'),
  description: yup.string().required('Please add a description'),
  requestedFeedback: yup
    .string()
    .required('Please add some notes about what feedback you’re looking for'),
  url: yup
    .string()
    .required('Please provide a link to your project in Codepen')
    .matches(
      CODEPEN_REGEX,
      'Please provide a valid link to Codepen e.g. https://codepen.io/[your-username]/pen/[pen-id]',
    ),
});

function normalizeCodepenURL(codepenURL: string) {
  const [, username, penID] = CODEPEN_REGEX.exec(codepenURL)!;
  return `https://codepen.io/${username}/pen/${penID}`;
}

type FormValues = yup.InferType<typeof schema>;

export type SolutionFormProps = {
  questionSlug: string;
};

export default function SolutionForm({ questionSlug }: SolutionFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { user, setUser } = UserState.useContainer();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
  });
  const router = useRouter();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsLoading(true);
    try {
      const result = (await apiFetch<Solution>('solutions', 'POST', {
        ...data,
        url: normalizeCodepenURL(data.url),
        questionSlug,
      }))!;

      if (!user) {
        return;
      }

      setUser({
        ...user,
        completedQuestions: [...user?.completedQuestions, questionSlug],
      });

      await router.push(`/solutions/${result.id}`);
    } catch (err) {
      toast.error('Sorry, something went wrong!');
      bugsnag.notify(err as Error);
    } finally {
      setIsLoading(false);
    }

    toast.success('Solution submitted!');
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <h2 className="text-2xl mb-8">Submit a solution</h2>
        <div className="mb-4">
          <InputField name="title" id="title" label="Title" />
        </div>
        <div className="mb-4">
          <InputField multiline id="description" label="Description" />
        </div>
        <div className="mb-4">
          <InputField
            multiline
            id="requestedFeedback"
            label="What do you want feedback on?"
          />
        </div>
        <div className="mb-4">
          <InputField id="url" label="Codepen URL" />
        </div>
        <Button type="submit" disabled={isLoading}>
          Save solution
        </Button>
      </form>
    </FormProvider>
  );
}
